'use client';

import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { toast } from 'sonner';
import { useForum } from '../../hooks/use-forum';
import { useAuthStore } from '../../stores';
import { useGetUserById } from '../../hooks/use-user';
import { useCheckUserLogin } from '../../hooks/use-user';
import { useCommunities } from '../../hooks/use-communities';
import { usePopularTags } from '../../hooks/use-tags';
import { TagBadge } from '../../components/ui/tag-badge';
import {
  ChevronDown,
  Plus,
  LayoutGrid,
  List as ListIcon,
  Search,
  Filter,
  MessageSquare,
  Award,
  ArrowLeft,
  Users,
  Shield,
  Tag,
  X,
  LogOut,
} from 'lucide-react';
import { ForumDiscussionCard } from '../../components/forum/forum-post-card';
import { MobileCategoriesDialog } from '../../components/forum/mobile-categories-dialog';
import { CategoryTabButton } from '../../components/forum/category-tab-button';
import CommunityStaffModal from '../../components/modals/community-staff-modal';
import {
  setSelectedCommunity,
  clearSelectedCommunity,
} from '../../utils/community-cookie';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Pagination } from '../../components/ui/pagination';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';

export default function ForumHomePage() {
  const { communitySlug } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(false);
  const [tagsCollapsed, setTagsCollapsed] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const hasShownRegistrationToast = useRef(false);

  // Fetch popular tags for sidebar filtering
  const { data: popularTags = [], isLoading: isLoadingTags } = usePopularTags(15);

  // Get wallet address from Passport SSO auth store (replaces useActiveAccount)
  const { walletAddress, isAuthenticated } = useAuthStore();
  const {
    data: user,
    isLoading: isLoadingUser,
    isFetched: isUserFetched,
    error: userError,
  } = useGetUserById(walletAddress);

  // Get community data by slug
  const {
    useGetCommunityBySlug,
    useGetCommunityDiscussions,
    useCheckMembership,
    useLeaveCommunity,
  } = useCommunities();
  const {
    data: community,
    isLoading: isLoadingCommunity,
    error: communityError,
  } = useGetCommunityBySlug(communitySlug);

  // Check if user is a member of the community
  const { data: membershipData, isLoading: isLoadingMembership } =
    useCheckMembership(community?.oid, user?.oid);
  const isMember = membershipData?.isMember || false;
  const canPost = membershipData?.canPost || false;
  const memberRole = membershipData?.role || null;

  // Leave community mutation
  const leaveCommunityMutation = useLeaveCommunity();

  // Set community cookie when community loads successfully
  useEffect(() => {
    if (community && !isLoadingCommunity) {
      setSelectedCommunity(community.slug, community.oid);
    }
  }, [community, isLoadingCommunity]);

  // Redirect to community selector if no community slug or community not found
  useEffect(() => {
    if (!communitySlug) {
      navigate('/forum');
    }
  }, [communitySlug, navigate]);

  // Redirect if community not found (after loading)
  useEffect(() => {
    if (!isLoadingCommunity && communityError && communitySlug) {
      // Community not found, redirect to selector
      navigate('/forum');
    }
  }, [isLoadingCommunity, communityError, communitySlug, navigate]);

  // Redirect unregistered or unverified users to complete registration
  useEffect(() => {
    // Only check if user is authenticated via Passport SSO
    if (!isAuthenticated) return;

    // Wait for user query to actually complete (isFetched is true after first successful/failed fetch)
    if (!isUserFetched) return;

    // Don't show toast if we've already shown it
    if (hasShownRegistrationToast.current) return;

    // Check if user doesn't exist OR exists but is not verified
    const userNotFound = userError?.response?.status === 404 || !user;
    const userNotVerified = user && !user.isVerified;

    if (userNotFound || userNotVerified) {
      hasShownRegistrationToast.current = true;
      toast.error('Complete Your Profile', {
        description: userNotVerified
          ? 'Please complete your profile verification to access community forums.'
          : 'Please complete your profile registration to access community forums.',
        action: {
          label: 'Complete Profile',
          onClick: () => navigate('/profile/complete-registration'),
        },
        duration: 5000,
      });
      navigate('/profile/complete-registration');
    }
  }, [isAuthenticated, user, userError, isUserFetched, navigate]);

  const { useGetAllDiscussions, upvoteInteraction, downvoteInteraction } =
    useForum();

  const upvoteMutation = upvoteInteraction();
  const downvoteMutation = downvoteInteraction();

  // Use community's custom discussion categories instead of global categories
  const categories = community?.discussionCategories || [];

  // Fetch discussions using useGetAllDiscussions, filter by selectedCategory and community
  const discussionsQuery = useGetAllDiscussions({
    ...(selectedCategory ? { category: selectedCategory } : {}),
    ...(community?.oid ? { communityOid: community.oid } : {}),
  });
  const discussions = discussionsQuery.data || [];
  const isLoading =
    discussionsQuery.isLoading ||
    discussionsQuery.isFetching ||
    isLoadingCommunity;

  // Handle category selection
  const handleCategorySelect = (categorySlug) => {
    setSelectedCategory(categorySlug);
    setCurrentPage(1); // Reset to first page when category changes
  };

  // Handle tag selection/deselection
  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [...prev, tagId];
    });
    setCurrentPage(1); // Reset to first page when tags change
  };

  // Clear all selected tags
  const clearAllTags = () => {
    setSelectedTags([]);
    setCurrentPage(1);
  };

  // Toggle categories visibility
  const toggleCategories = () => {
    setCategoriesCollapsed(!categoriesCollapsed);
  };

  // Toggle tags visibility
  const toggleTags = () => {
    setTagsCollapsed(!tagsCollapsed);
  };

  // Toggle mobile categories dialog
  const toggleMobileCategories = () => {
    setMobileCategoriesOpen(!mobileCategoriesOpen);
  };

  // Handle discussion voting
  const handleUpvote = async (discussionId) => {
    if (!user) return;

    await upvoteMutation.mutateAsync({
      discussionId,
      uOID: user.oid,
      creator: walletAddress,
    });
  };

  const handleDownvote = async (discussionId) => {
    if (!user) return;
    await downvoteMutation.mutateAsync({
      discussionId,
      uOID: user.oid,
      creator: walletAddress,
    });
  };

  // Handle leaving community
  const handleLeaveCommunity = async () => {
    if (!community?.oid || !walletAddress) return;

    try {
      await leaveCommunityMutation.mutateAsync({
        communityOid: community.oid,
        creator: walletAddress,
      });
      toast.success('Left community', {
        description: `You have left ${community.name}. You cannot rejoin without permission from an admin.`,
      });
      setShowLeaveDialog(false);
      // Navigate back to community selector
      clearSelectedCommunity();
      navigate('/forum');
    } catch (error) {
      toast.error('Failed to leave community', {
        description: error.response?.data?.error || error.message,
      });
    }
  };

  // Find the selected category object for heading display
  const selectedCategoryObj = categories.find(
    (cat) => (cat.slug || cat.name) === selectedCategory
  );

  // Filter discussions by search query and selected tags
  const filteredDiscussions = discussions.filter((d) => {
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        d.title.toLowerCase().includes(query) ||
        d.description?.toLowerCase().includes(query) ||
        d.category?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Tag filter - discussion must have ALL selected tags
    if (selectedTags.length > 0) {
      const discussionTags = d.tags || [];
      const hasAllSelectedTags = selectedTags.every((tagId) =>
        discussionTags.includes(tagId)
      );
      if (!hasAllSelectedTags) return false;
    }

    return true;
  });

  // Sort discussions: pinned first (in this category), then newest to oldest
  const sortedDiscussions = [
    // Pinned discussions for the selected category, sorted by pinnedAt desc
    ...filteredDiscussions
      .filter(
        (d) =>
          d.pinned &&
          (selectedCategory === '' || d.category === selectedCategory)
      )
      .sort((a, b) => new Date(b.pinnedAt || 0) - new Date(a.pinnedAt || 0)),
    // Non-pinned discussions, sorted by createdAt desc
    ...filteredDiscussions
      .filter(
        (d) =>
          !d.pinned ||
          (selectedCategory !== '' && d.category !== selectedCategory)
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
  ];

  // Pagination calculations
  const totalPages = Math.ceil(sortedDiscussions.length / itemsPerPage);
  const paginatedDiscussions = sortedDiscussions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of discussions list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Block rendering if user is authenticated but registration status is unknown
  // This prevents the flash of forum content before redirect
  if (isAuthenticated && !isUserFetched) {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Block rendering if user is authenticated but doesn't exist or is not verified (will redirect via useEffect)
  if (isAuthenticated && isUserFetched && (!user || !user.isVerified)) {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400">Redirecting to registration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-100 font-sans selection:bg-purple-500/30 flex flex-col">
      {/* Background Gradients */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>
      <div className="fixed inset-0 z-0 pointer-events-none bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

      {/* Navbar - Full Width at Top */}
      <div className="sticky top-0 z-40 w-full bg-black/60 backdrop-blur-xl border-b border-white/5">
        <Navbar />
      </div>

      <div className="flex flex-1 relative z-10">
        {/* Mobile Categories Tab Button */}
        <CategoryTabButton
          onClick={toggleMobileCategories}
          isOpen={mobileCategoriesOpen}
        />

        {/* Mobile Categories Dialog */}
        <MobileCategoriesDialog
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
          isOpen={mobileCategoriesOpen}
          onClose={() => setMobileCategoriesOpen(false)}
        />

        {/* Left sidebar - Hidden on mobile */}
        <aside
          className={`hidden md:flex flex-col sticky top-[73px] h-[calc(100vh-73px)] bg-black/40 backdrop-blur-xl border-r border-white/5 overflow-y-auto custom-scrollbar transition-all duration-300 ${
            isSidebarCollapsed ? 'w-20' : 'w-72'
          }`}
        >
          <div className="p-4 flex flex-col h-full">
            {/* Collapse Toggle Button */}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="absolute -right-3 top-6 z-50 bg-black border border-violet-500/30 text-violet-400 rounded-full p-1 shadow-[0_0_10px_rgba(139,92,246,0.2)] hover:shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:text-white hover:border-violet-400 transition-all duration-300"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${
                  isSidebarCollapsed ? '-rotate-90' : 'rotate-90'
                }`}
              />
            </button>

            <div className="space-y-6 mt-4">
              {/* Community Info Header */}
              {community && !isSidebarCollapsed && (
                <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-violet-500/20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-sm font-bold">
                      {community.name?.charAt(0) || 'C'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">
                        {community.name}
                      </h3>
                      <p className="text-xs text-zinc-500">
                        {community.category}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-zinc-400">
                      <Users className="w-3 h-3" />
                      <span>{community.memberCount || 0} members</span>
                    </div>
                    <button
                      onClick={() => setShowStaffModal(true)}
                      className="flex items-center gap-1 text-zinc-400 hover:text-violet-400 transition-colors"
                    >
                      <Shield className="w-3 h-3" />
                      <span>Staff</span>
                    </button>
                  </div>
                  {/* Leave Community Button - only show for members who are not owners */}
                  {isMember && memberRole !== 'owner' && (
                    <button
                      onClick={() => setShowLeaveDialog(true)}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20 transition-all text-xs"
                    >
                      <LogOut className="w-3 h-3" />
                      <span>Leave Community</span>
                    </button>
                  )}
                </div>
              )}

              <div>
                {!isSidebarCollapsed && (
                  <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-2">
                    Menu
                  </h3>
                )}
                <nav className="space-y-1">
                  <button
                    onClick={() => {
                      clearSelectedCommunity();
                      navigate('/forum');
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors ${
                      isSidebarCollapsed ? 'justify-center' : ''
                    }`}
                    title={isSidebarCollapsed ? 'Switch Community' : ''}
                  >
                    <ArrowLeft className="h-5 w-5 flex-shrink-0" />
                    {!isSidebarCollapsed && <span>Switch Community</span>}
                  </button>
                  <Link
                    to={`/forum/${communitySlug}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 text-white font-medium ${
                      isSidebarCollapsed ? 'justify-center' : ''
                    }`}
                    title={isSidebarCollapsed ? 'Discussions' : ''}
                  >
                    <LayoutGrid className="h-5 w-5 text-violet-400 flex-shrink-0" />
                    {!isSidebarCollapsed && <span>Discussions</span>}
                  </Link>
                  <Link
                    to="/profile"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors ${
                      isSidebarCollapsed ? 'justify-center' : ''
                    }`}
                    title={isSidebarCollapsed ? 'My Profile' : ''}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0"
                    >
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    {!isSidebarCollapsed && <span>My Profile</span>}
                  </Link>
                  <Link
                    to={`/forum/${communitySlug}/badges`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors ${
                      isSidebarCollapsed ? 'justify-center' : ''
                    }`}
                    title={isSidebarCollapsed ? 'Badges' : ''}
                  >
                    <Award className="h-5 w-5 flex-shrink-0" />
                    {!isSidebarCollapsed && <span>Badges</span>}
                  </Link>
                </nav>
              </div>

              <div>
                <div
                  className={`flex items-center ${
                    isSidebarCollapsed ? 'justify-center' : 'justify-between'
                  } mb-3 px-2 cursor-pointer group`}
                  onClick={toggleCategories}
                  title={isSidebarCollapsed ? 'Categories' : ''}
                >
                  {!isSidebarCollapsed && (
                    <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider group-hover:text-zinc-300 transition-colors">
                      Categories
                    </h3>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 text-zinc-500 transition-transform ${
                      categoriesCollapsed ? '-rotate-90' : ''
                    } ${isSidebarCollapsed ? 'hidden' : ''}`}
                  />
                  {isSidebarCollapsed && (
                    <ListIcon className="h-4 w-4 text-zinc-500" />
                  )}
                </div>

                {!categoriesCollapsed && (
                  <div className="space-y-1">
                    <button
                      onClick={() => handleCategorySelect('')}
                      className={`w-full text-left py-2 px-3 rounded-lg flex items-center gap-3 transition-all duration-200 ${
                        selectedCategory === ''
                          ? 'bg-violet-500/10 text-violet-200 border border-violet-500/20'
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                      title={isSidebarCollapsed ? 'All Discussions' : ''}
                    >
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          selectedCategory === ''
                            ? 'bg-violet-400'
                            : 'bg-zinc-600'
                        }`}
                      />
                      {!isSidebarCollapsed && (
                        <span className="text-sm font-medium">
                          All Discussions
                        </span>
                      )}
                    </button>

                    {categories.map((category, index) => {
                      const categoryKey =
                        category.slug || category._id || category.name || index;
                      return (
                        <button
                          key={categoryKey}
                          onClick={() =>
                            handleCategorySelect(category.slug || category.name)
                          }
                          className={`w-full text-left py-2 px-3 rounded-lg flex items-center gap-3 transition-all duration-200 ${
                            selectedCategory ===
                            (category.slug || category.name)
                              ? 'bg-white/10 text-white border border-white/10'
                              : 'text-zinc-400 hover:text-white hover:bg-white/5'
                          } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                          title={isSidebarCollapsed ? category.name : ''}
                        >
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: category.color || '#3B82F6',
                              boxShadow:
                                selectedCategory ===
                                (category.slug || category.name)
                                  ? `0 0 8px ${category.color || '#3B82F6'}`
                                  : 'none',
                            }}
                          />
                          {!isSidebarCollapsed && (
                            <span className="text-sm font-medium truncate">
                              {category.name}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Tags Section */}
              <div>
                <div
                  className={`flex items-center ${
                    isSidebarCollapsed ? 'justify-center' : 'justify-between'
                  } mb-3 px-2 cursor-pointer group`}
                  onClick={toggleTags}
                  title={isSidebarCollapsed ? 'Tags' : ''}
                >
                  {!isSidebarCollapsed && (
                    <div className="flex items-center gap-2">
                      <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider group-hover:text-zinc-300 transition-colors">
                        Filter by Tags
                      </h3>
                      {selectedTags.length > 0 && (
                        <span className="text-xs bg-violet-500/20 text-violet-300 px-1.5 py-0.5 rounded-full">
                          {selectedTags.length}
                        </span>
                      )}
                    </div>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 text-zinc-500 transition-transform ${
                      tagsCollapsed ? '-rotate-90' : ''
                    } ${isSidebarCollapsed ? 'hidden' : ''}`}
                  />
                  {isSidebarCollapsed && (
                    <Tag className="h-4 w-4 text-zinc-500" />
                  )}
                </div>

                {!tagsCollapsed && !isSidebarCollapsed && (
                  <div className="space-y-2">
                    {/* Clear tags button */}
                    {selectedTags.length > 0 && (
                      <button
                        onClick={clearAllTags}
                        className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors px-2 py-1"
                      >
                        <X className="h-3 w-3" />
                        Clear all filters
                      </button>
                    )}

                    {/* Tag list */}
                    {isLoadingTags ? (
                      <div className="space-y-2 px-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className="h-6 bg-zinc-800/50 rounded-full animate-pulse"
                          />
                        ))}
                      </div>
                    ) : popularTags.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 px-2">
                        {popularTags.map((tag) => {
                          const tagId = tag.id || tag.tagId || tag.slug;
                          const isSelected = selectedTags.includes(tagId);
                          return (
                            <button
                              key={tagId}
                              onClick={() => handleTagToggle(tagId)}
                              className="transition-all duration-200"
                            >
                              <TagBadge
                                tag={tag}
                                size="sm"
                                className={`cursor-pointer transition-all ${
                                  isSelected
                                    ? 'ring-2 ring-violet-500 ring-offset-1 ring-offset-black scale-105'
                                    : 'opacity-70 hover:opacity-100 hover:scale-105'
                                }`}
                              />
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-600 px-2">
                        No tags available
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`mt-auto pt-6 border-t border-white/5 ${
                isSidebarCollapsed ? 'hidden' : ''
              }`}
            >
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <span>© 2024 Talktrail</span>
                <span>•</span>
                <Link to="/privacy" className="hover:text-zinc-300">
                  Privacy
                </Link>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-8 max-w-7xl mx-auto w-full min-w-0 transition-all duration-300">
          {/* Hero Section / Header */}
          <div className="mb-8">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/40 backdrop-blur-sm mb-6">
              {/* Category Image Background */}
              <div className="absolute inset-0 z-0">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selectedCategoryObj?.image || 'default'}
                    src={
                      selectedCategoryObj?.image ||
                      'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&auto=format&fit=crop&q=80'
                    }
                    alt={selectedCategoryObj?.name || 'Community Forum'}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 0.6, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
              </div>

              {/* Hero Content */}
              <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 md:p-8">
                <div className="flex-1">
                  <motion.h1
                    key={
                      selectedCategoryObj?.name || community?.name || 'forum'
                    }
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight"
                  >
                    {selectedCategoryObj
                      ? selectedCategoryObj.name
                      : community?.name || 'Community Forum'}
                  </motion.h1>
                  <motion.p
                    key={
                      selectedCategoryObj?.description ||
                      community?.description ||
                      'desc'
                    }
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="text-zinc-300 max-w-2xl text-lg"
                  >
                    {selectedCategoryObj
                      ? selectedCategoryObj.description
                      : community?.description ||
                        'Join the conversation, share your knowledge, and connect with the community.'}
                  </motion.p>
                  {/* Filter status info */}
                  {(selectedCategoryObj || selectedTags.length > 0) && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="mt-4 flex flex-wrap items-center gap-2"
                    >
                      {selectedCategoryObj && (
                        <>
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor:
                                selectedCategoryObj.color || '#8B5CF6',
                            }}
                          />
                          <span className="text-sm text-zinc-400">
                            {filteredDiscussions.length} discussion
                            {filteredDiscussions.length !== 1 ? 's' : ''} in this
                            category
                          </span>
                        </>
                      )}
                      {selectedTags.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {selectedCategoryObj && (
                            <span className="text-zinc-600">•</span>
                          )}
                          <span className="text-sm text-zinc-400 flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            Filtering by {selectedTags.length} tag
                            {selectedTags.length !== 1 ? 's' : ''}
                          </span>
                          <button
                            onClick={clearAllTags}
                            className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                          >
                            <X className="h-3 w-3" />
                            Clear
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                {user && user.isVerified && canPost && (
                  <Button
                    asChild
                    size="lg"
                    className="bg-white text-black hover:bg-zinc-200 font-semibold shadow-lg shadow-white/5 transition-all hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                  >
                    <Link
                      to={`/forum/${communitySlug}/discussion/create`}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-5 w-5" />
                      <span>New Discussion</span>
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {/* Controls Bar */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-zinc-900/40 border border-white/5 p-4 rounded-xl backdrop-blur-sm">
              <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder="Search discussions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-zinc-600 focus:border-violet-500/50 focus:ring-violet-500/20"
                />
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="flex items-center gap-2 text-sm text-zinc-400 bg-black/20 px-3 py-2 rounded-lg border border-white/5">
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Show:</span>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => {
                      setItemsPerPage(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[70px] h-6 bg-transparent border-0 p-0 text-white focus:ring-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Discussions List */}
          <div className="space-y-4 min-h-[400px]">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-40 bg-zinc-900/30 border border-white/5 rounded-xl animate-pulse"
                  />
                ))}
              </div>
            ) : paginatedDiscussions.length > 0 ? (
              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {paginatedDiscussions.map((discussion, index) => (
                    <motion.div
                      key={discussion.oid}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <ForumDiscussionCard
                        discussion={discussion}
                        category={categories.find(
                          (cat) =>
                            cat.slug === discussion.category ||
                            cat.name === discussion.category
                        )}
                        categories={categories}
                        onUpvote={handleUpvote}
                        onDownvote={handleDownvote}
                        user={user}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-white/10 rounded-xl bg-white/5">
                <div className="h-16 w-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
                  <MessageSquare className="h-8 w-8 text-zinc-600" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  No discussions found
                </h3>
                <p className="text-zinc-400 max-w-md mb-6">
                  {searchQuery
                    ? `No results found for "${searchQuery}". Try a different search term.`
                    : 'There are no discussions in this category yet. Be the first to start a conversation!'}
                </p>
                {user && user.isVerified && (
                  <Button
                    asChild
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <Link to={`/forum/${communitySlug}/discussion/create`}>
                      Start a Discussion
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageCount={true}
              />
            </div>
          )}
        </main>
      </div>

      {/* Community Staff Modal */}
      {community && (
        <CommunityStaffModal
          communityOid={community.oid}
          open={showStaffModal}
          onOpenChange={setShowStaffModal}
        />
      )}

      {/* Leave Community Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Leave {community?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to leave this community? Once you leave,{' '}
              <span className="text-red-400 font-medium">
                you cannot rejoin without permission from an admin or moderator
              </span>
              . All your posts and comments will remain visible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveCommunity}
              disabled={leaveCommunityMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {leaveCommunityMutation.isPending ? 'Leaving...' : 'Leave Community'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="md:hidden">
        <Footer />
      </div>
    </div>
  );
}
